import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { PageDetailLayout } from '@/shared/layout';
import { useConstructionIssueDetail } from './hooks/useIssues';
import { Button, Badge, Card, FeaturePlaceholder, Alert, KPICard, Table, Avatar } from '@/components/ui';
import { AuthButton } from '@/core/permissions/AuthButton';
import { useAuth } from '@/core/auth/AuthContext';
import { toast } from 'sonner';

const statusColors: Record<string, string> = {
  open: 'bg-red-100 text-red-600',
  triaged: 'bg-amber-100 text-amber-600',
  assigned: 'bg-blue-100 text-blue-600',
  in_progress: 'bg-indigo-100 text-indigo-600',
  waiting_material: 'bg-slate-100 text-slate-600',
  waiting_approval: 'bg-violet-100 text-violet-600',
  rework_created: 'bg-orange-100 text-orange-600',
  resolved: 'bg-emerald-100 text-emerald-600',
  verified: 'bg-emerald-600 text-white',
  closed: 'bg-slate-900 text-white',
  rejected: 'bg-slate-200 text-slate-500',
  cancelled: 'bg-slate-200 text-slate-400',
};

const statusLabels: Record<string, string> = {
  open: 'Mới mở',
  triaged: 'Đã phân loại',
  assigned: 'Đã gán',
  in_progress: 'Đang xử lý',
  waiting_material: 'Chờ vật tư',
  waiting_approval: 'Chờ phê duyệt',
  rework_created: 'Đang sửa lỗi (Rework)',
  resolved: 'Đã xử lý',
  verified: 'Đã xác minh',
  closed: 'Đã đóng',
  rejected: 'Từ chối',
  cancelled: 'Đã hủy',
};

export default function IssueDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { profile } = useAuth();

  const { issue: item, comments, isLoading, updateIssue } = useConstructionIssueDetail(id);

  const [activeTab, setActiveTab] = useState('overview');

  if (isLoading) return <div className="p-12 text-center text-slate-500 text-sm">Đang tải chi tiết sự cố...</div>;
  if (!item) return <div className="p-12 text-center text-red-500 font-bold">Không tìm thấy thông tin sự cố</div>;

  const handleStatusChange = async (newStatus: string) => {
    try {
      await updateIssue({ status_code: newStatus as any });
      toast.success(`Đã cập nhật trạng thái sự cố sang ${statusLabels[newStatus]}`);
    } catch (error: any) {
      toast.error('Lỗi: ' + error.message);
    }
  };

  const tabs = [
    { value: 'overview', label: 'Tổng quan' },
    { value: 'photos', label: 'Hình ảnh hiện trường' },
    { value: 'relations', label: 'Liên kết thực thể' },
    { value: 'rework', label: 'Lệnh sửa lỗi (Rework)' },
    { value: 'costs', label: 'Chi phí ảnh hưởng' },
    { value: 'comments', label: 'Thảo luận' },
    { value: 'logs', label: 'Nhật ký xử lý' },
  ];

  return (
    <PageDetailLayout
      title={item.title}
      description={
        <div className="flex items-center gap-3 mt-1">
          <Badge className={`${statusColors[item.status_code] || 'bg-slate-100 text-slate-600'} border-transparent text-[10px] font-bold `}>
            {statusLabels[item.status_code] || item.status_code}
          </Badge>
          <span className="text-sm text-slate-400 font-mono">#{item.issue_code}</span>
          <span className="text-slate-300">|</span>
          <span className="text-sm text-slate-500 flex items-center gap-1">
            <i className="ri-alarm-warning-line" />
            {item.severity_code.toUpperCase()}
          </span>
        </div>
      }
      breadcrumbs={[
        { label: 'Sự cố thi công', path: '/construction/issues' },
        { label: item.issue_code }
      ]}
      headerActions={
        <div className="flex gap-2">
          {item.status_code === 'open' && (
            <AuthButton module="quality" action="update" variant="primary" icon="ri-user-add-line" onClick={() => handleStatusChange('assigned')}>
              Giao việc xử lý
            </AuthButton>
          )}
          {(item.status_code === 'assigned' || item.status_code === 'in_progress') && (
            <AuthButton module="quality" action="create" variant="info" icon="ri-tools-line">
              Tạo lệnh Rework
            </AuthButton>
          )}
          {item.status_code === 'resolved' && (
            <AuthButton module="quality" action="approve" variant="success" icon="ri-check-double-line" onClick={() => handleStatusChange('verified')}>
              Xác minh xử lý
            </AuthButton>
          )}
          <Button variant="outline" icon="ri-more-2-fill" />
        </div>
      }
      tabs={tabs}
      activeTab={activeTab}
      onTabChange={setActiveTab}
    >
      <div className="space-y-6">
        {activeTab === 'overview' && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2 space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <KPICard title="Mức độ rủi ro" value={item.severity_code.toUpperCase()} icon="ri-error-warning-line" variant={item.severity_code === 'critical' ? 'danger' : 'warning'} />
                <KPICard title="Hạn xử lý (SLA)" value={item.sla_due_at ? new Date(item.sla_due_at).toLocaleDateString('vi-VN') : 'N/A'} icon="ri-timer-line" variant="info" />
                <KPICard title="Thảo luận" value={comments.length} icon="ri-chat-3-line" variant="primary" />
              </div>

              <Card className="p-6">
                <h3 className="font-bold text-slate-900 mb-4 flex items-center gap-2">
                  <i className="ri-text-snippet text-os-primary" />
                  Chi tiết sự cố & Nguyên nhân
                </h3>
                <p className="text-sm text-slate-600 leading-relaxed mb-6 bg-slate-50 p-4 rounded-2xl border border-slate-100">
                  {item.description || 'Không có mô tả chi tiết cho sự cố này.'}
                </p>

                {item.root_cause_note && (
                  <div className="bg-amber-50 p-4 rounded-2xl border border-amber-100 mb-6">
                    <p className="text-[10px] text-amber-600 font-bold  mb-1">Phân tích nguyên nhân gốc rễ (Root Cause)</p>
                    <p className="text-sm text-slate-700 italic">"{item.root_cause_note}"</p>
                  </div>
                )}

                <div className="grid grid-cols-1 md:grid-cols-2 gap-y-4 gap-x-12 mt-4 pt-6 border-t border-slate-50">
                  <div className="flex justify-between border-b border-slate-50 pb-2">
                    <span className="text-sm text-slate-500">Phân loại</span>
                    <span className="text-sm font-bold text-slate-700">{item.issue_type}</span>
                  </div>
                  <div className="flex justify-between border-b border-slate-50 pb-2">
                    <span className="text-sm text-slate-500">Khu vực (Zone)</span>
                    <span className="text-sm font-bold text-os-primary underline">{item.zone?.name || '---'}</span>
                  </div>
                  <div className="flex justify-between border-b border-slate-50 pb-2">
                    <span className="text-sm text-slate-500">Người báo cáo</span>
                    <span className="text-sm font-bold text-slate-700">{item.reporter?.full_name}</span>
                  </div>
                  <div className="flex justify-between border-b border-slate-50 pb-2">
                    <span className="text-sm text-slate-500">Ngày báo cáo</span>
                    <span className="text-sm font-bold text-slate-700">{new Date(item.reported_at).toLocaleString('vi-VN')}</span>
                  </div>
                </div>
              </Card>

              <Card className="p-6">
                <h3 className="font-bold text-slate-900 mb-4 flex items-center gap-2">
                  <i className="ri-shield-line text-slate-400" />
                  Phân tích rủi ro & Tác động
                </h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className={`p-3 rounded-2xl border flex flex-col items-center gap-1 ${item.risk_to_safety ? 'bg-red-50 border-red-100 text-red-600' : 'bg-slate-50 border-slate-100 text-slate-400 opacity-50'}`}>
                    <i className="ri-heart-pulse-line text-lg" />
                    <span className="text-[10px] font-bold ">An toàn</span>
                  </div>
                  <div className={`p-3 rounded-2xl border flex flex-col items-center gap-1 ${item.risk_to_quality ? 'bg-orange-50 border-orange-100 text-orange-600' : 'bg-slate-50 border-slate-100 text-slate-400 opacity-50'}`}>
                    <i className="ri-shield-check-line text-lg" />
                    <span className="text-[10px] font-bold ">Chất lượng</span>
                  </div>
                  <div className={`p-3 rounded-2xl border flex flex-col items-center gap-1 ${item.risk_to_schedule ? 'bg-blue-50 border-blue-100 text-blue-600' : 'bg-slate-50 border-slate-100 text-slate-400 opacity-50'}`}>
                    <i className="ri-calendar-todo-line text-lg" />
                    <span className="text-[10px] font-bold ">Tiến độ</span>
                  </div>
                  <div className={`p-3 rounded-2xl border flex flex-col items-center gap-1 ${item.risk_to_cost ? 'bg-emerald-50 border-emerald-100 text-emerald-600' : 'bg-slate-50 border-slate-100 text-slate-400 opacity-50'}`}>
                    <i className="ri-money-dollar-circle-line text-lg" />
                    <span className="text-[10px] font-bold ">Chi phí</span>
                  </div>
                </div>
              </Card>
            </div>

            <div className="space-y-6">
              <Card className="p-6 bg-slate-900 text-white">
                <h4 className="font-bold mb-4 flex items-center gap-2 text-os-primary">
                  <i className="ri-user-settings-line" />
                  Nhân sự phụ trách
                </h4>
                <div className="space-y-4">
                  <div className="flex items-center gap-3 p-3 bg-slate-800 rounded-2xl border border-slate-700">
                    <Avatar name={item.assignee?.full_name} src={item.assignee?.avatar_url} size="sm" />
                    <div className="flex flex-col">
                      <p className="text-[10px] text-slate-400 font-bold ">Người xử lý chính</p>
                      <p className="text-sm font-bold text-slate-200">{item.assignee?.full_name || 'Đang chờ gán...'}</p>
                    </div>
                  </div>
                  <div className="p-3 bg-slate-800 rounded-2xl border border-slate-700">
                    <p className="text-[10px] text-slate-400 font-bold  mb-1">Tổ đội thi công</p>
                    <p className="text-sm font-bold text-slate-200">{item.assigned_team?.name || '---'}</p>
                  </div>
                </div>
              </Card>

              <Card className="p-6">
                <h4 className="font-bold text-slate-900 mb-4 flex items-center gap-2">
                  <i className="ri-chat-3-line text-slate-400" />
                  Thảo luận mới nhất
                </h4>
                <div className="space-y-4 max-h-60 overflow-y-auto pr-2 custom-scrollbar">
                  {comments.length > 0 ? comments.slice(-3).map(comment => (
                    <div key={comment.id} className="flex flex-col gap-1 p-2 bg-slate-50 rounded-xl border border-slate-100">
                      <div className="flex justify-between items-center">
                        <span className="text-[10px] font-bold text-slate-700">{comment.author?.full_name}</span>
                        <span className="text-[9px] text-slate-400">{new Date(comment.created_at).toLocaleTimeString('vi-VN')}</span>
                      </div>
                      <p className="text-[11px] text-slate-600 line-clamp-2">{comment.comment}</p>
                    </div>
                  )) : (
                    <p className="text-xs text-slate-500 italic">Chưa có thảo luận nào.</p>
                  )}
                </div>
                <Button variant="ghost" size="sm" className="w-full mt-4 text-[10px]" onClick={() => setActiveTab('comments')}>
                  Tất cả bình luận
                </Button>
              </Card>

              {item.status_code === 'verified' && (
                <Alert variant="success" title="Đã xác minh" className="text-xs">
                  Sự cố đã được xác minh xử lý thành công bởi bộ phận QA/QC.
                </Alert>
              )}
            </div>
          </div>
        )}

        {(activeTab === 'photos' || activeTab === 'relations' || activeTab === 'rework' || activeTab === 'costs' || activeTab === 'comments' || activeTab === 'logs') && (
          <FeaturePlaceholder
            title={tabs.find(t => t.value === activeTab)?.label || 'Tính năng'}
            description="Dữ liệu đang được đồng bộ từ nhật ký hiện trường, phân tích nguyên nhân gốc rễ và các biện pháp khắc phục/phòng ngừa."
          />
        )}
      </div>
    </PageDetailLayout>
  );
}
